/* "The Chenda Goes Global" — special editorial article linked from the Kerala travel guide. */
const U = (id) => `https://images.unsplash.com/${id}?w=1600&q=80&auto=format&fit=crop`;

export const THE_CHENDA = {
  slug: "the-chenda",
  title: "The Chenda Goes Global",
  subtitle: "Kerala's Thunderous Drum Finds a New Beat Across the World",
  heading: "The Chenda Goes Global: Kerala's Thunderous Drum Finds a New Beat Across the World",
  hero: U("photo-1735328477415-9dc860ea211f"),
  heroCap: "A chenda player commands the crowd — the sound that announces Kerala anywhere in the world.",
  intro: `From Kerala's temple courtyards and legendary Poorams to churches, community halls and enormous Malayalee gatherings in America, the chenda has evolved from a traditional percussion instrument into a powerful global expression of Kerala identity.

There are sounds that instantly transport a Malayalee home.

The hiss of the monsoon on a tiled roof. The call of a temple bell in the morning. The crackle of a festival loudspeaker somewhere in the distance.

And then there is the chenda.

You rarely hear a chenda quietly. It announces itself.

Thakida-thakida-thom!

The first strokes command attention. A few more invite excitement. Then another chenda joins, followed by another and another. Soon the rhythm seems to travel through the ground as much as through the air.

For generations, that unmistakable sound belonged primarily to Kerala. Today, it can erupt in Pennsylvania, New Jersey, Texas, London, Dublin, Melbourne, Toronto, Dubai or almost anywhere a sufficiently enthusiastic group of Malayalees has gathered.

And increasingly, those holding the chenda sticks are not hereditary percussionists or professional musicians. They are doctors, engineers, nurses, IT professionals, businesspeople, students, retirees, homemakers and children.

Something remarkable has happened to one of Kerala's oldest and loudest cultural traditions.

The chenda has emigrated with the Malayalee.`,
  sections: [
    {
      h: "A Drum That Became the Sound of Kerala",
      img: U("photo-1723879867254-b698fb084bfd"),
      cap: "In a melam, the individual drummer becomes part of something much larger.",
      t: `The chenda is a cylindrical percussion instrument traditionally played vertically, usually with sticks. Its penetrating tone makes it particularly suited to outdoor performance, where dozens—or even hundreds—of musicians can create extraordinary rhythmic intensity.

But calling the chenda simply a "drum" hardly captures its place in Kerala culture.

It has long been intertwined with Kerala's temples, religious observances, processions, Kathakali and other performing traditions, and especially the great percussion ensembles known as melams.

In a melam, the individual drummer becomes part of something much larger. Chendas interact with instruments such as the ilathalam or cymbals, kombu and kuzhal, building increasingly complex rhythmic patterns.

The music can begin with an almost deceptive discipline. Then it grows.

The tempo rises.
The repetitions tighten.
Dozens of musicians seem to breathe as one.
The audience begins moving with them.

And eventually the distinction between performer and spectator almost disappears.`,
    },
    {
      h: "Thrissur Pooram: Where the Chenda Becomes Thunder",
      img: U("photo-1784619491771-acb3bc66329d"),
      cap: "Percussion in procession — festival ensembles can bring together hundreds of artistes.",
      t: `Perhaps nowhere is the emotional power of Kerala percussion demonstrated more spectacularly than at Thrissur Pooram.

Held around the Vadakkumnathan Temple at Thekkinkadu Maidanam, Thrissur Pooram dates back more than two centuries and was initiated under Shakthan Thampuran, the Maharaja of Cochin. The festival brings together participating temples in an enormous celebration of processions, elephants, colourful parasols, fireworks and percussion.

One of its legendary moments is the Ilanjithara Melam. According to Thrissur's District Tourism Promotion Council, around 250 artistes participate, using traditional instruments including chenda, kurumkuzhal, kombu and ilathalam.

Anyone who has stood among thousands of people while a great melam reaches its crescendo understands why recorded music cannot quite reproduce the experience.

You do not merely listen to it.
You feel it.

The rhythms pass through the crowd. Hands rise. Heads move. Strangers smile at one another. For a few minutes, an enormous gathering behaves almost like a single organism responding to rhythm.

The Government of India's tourism portal describes the Ilanjithara Melam as an orchestral performance involving hundreds of artists, mirrored by thousands of spectators swaying with the music.`,
    },
    {
      h: "From Temple Courtyards to Church Grounds",
      t: `One of the most fascinating chapters in the chenda's story is how far it has travelled beyond its original ceremonial settings.

Although deeply associated with Kerala's Hindu temple traditions, chenda performances have increasingly become part of the broader cultural vocabulary of Kerala.

Today the instrument can be heard at cultural festivals, school and college programs, tourism events, receptions and public celebrations. Chenda ensembles have also become familiar at celebrations connected with Kerala's Christian communities and churches.

That transition says something important about Kerala.

An art form can originate within a particular religious or ritual tradition and, over generations, acquire a wider cultural identity.

For many Malayalees today, hearing a chenda does not communicate only one religion.
It communicates Kerala.`,
    },
    {
      h: "Then the Malayalee Took the Chenda Abroad",
      img: U("photo-1752760023111-aed0c41f11f9"),
      cap: "What was once the territory of trained performers has become a joyful community activity.",
      t: `Malayalees have been migrating around the world for generations. They carried Malayalam, coconut oil and curry leaves; Onam and Vishu; mundus and kasavu sarees; parish feasts and temple festivals; banana chips and achappam.

Eventually, they carried the chenda too.

And they did not bring just one.

Across the United States, Canada, the United Kingdom, Europe, Australia and the Middle East, Malayalee associations and cultural organizations increasingly organize chenda groups and training programs.

In many communities, someone who learned chenda in Kerala begins teaching a few friends.

Five learners become fifteen.
Fifteen become fifty.
Someone orders more chendas from Kerala.
Children want to learn.
Women join.
Parents join their children.
People in their 50s and 60s decide that perhaps it isn't too late to pick up a pair of sticks.

Soon a community has its own chenda melam team.

What was once primarily the territory of highly trained traditional performers has also become a diaspora recreational activity and social experience.

It may be one of Kerala's noisiest exports—but it is certainly among its happiest.`,
    },
    {
      h: "A New Malayalee Obsession?",
      t: `There is also something wonderfully humorous about the phenomenon.

The expatriate Malayalee has always been talented at recreating Kerala abroad.

Give Malayalees a community hall and eventually there will be an Onam celebration.
Give them a kitchen and there will be sambar.
Give them a microphone and there will probably be a speech.

Give 50 Malayalees chendas, however, and the neighbourhood may soon know that Kerala has arrived.

For many participants, chenda practice has become a pastime comparable to joining a choir, sports club or dance group.

Except considerably louder.

And unlike many traditional cultural activities in which people primarily watch professionals perform, chenda encourages participation. The person who watched melams as a child in Kerala can, decades later in America or Britain, become part of one.

There is also an increasingly visible participation of women and younger generations, helping move the tradition beyond some of its older social boundaries.

Perhaps the greatest irony is that organized recreational chenda groups sometimes appear extraordinarily enthusiastic in the diaspora. Malayalees who may have taken the sound for granted while living in Kerala suddenly rediscover it thousands of miles from home.

Distance has a way of making culture precious.`,
    },
    {
      h: "Pennsylvania: When 500-Plus Chenda Players Made History",
      t: `That global movement produced an extraordinary spectacle in Pennsylvania in August 2026.

At the FOKANA International Convention at Kalahari Resorts in Pennsylvania's Pocono Mountains, more than 500 chenda performers assembled for a massive chenda melam connected with a Guinness World Records achievement.

The gathering was part of an extraordinary celebration of Kerala culture in which chenda melam and a mass Mohiniyattam performance achieved Guinness recognition. Manorama reported on August 11, 2026 that more than 500 performers participated in the chenda event, while more than 1,000 dancers participated in the Mohiniyattam performance.

Think about what that represents.

Hundreds of chendas—an instrument rooted in Kerala's cultural landscape—playing together thousands of miles away in the mountains of Pennsylvania.

Not in Thrissur.
Not at a Kerala temple.
Not beside coconut palms.

But in the Poconos.

It is difficult to imagine a better symbol of how Kerala culture has travelled.`,
    },
    {
      h: "The Chenda as a Connection to Home",
      img: U("photo-1783255333710-dedc88e28cc8"),
      cap: "Rhythm does not require translation — grandparent and grandchild can play in the same ensemble.",
      t: `The global popularity of chenda is about much more than making noise.

For immigrants, cultural traditions can provide an emotional bridge between two worlds.

A Malayalee child growing up in America, Britain or Australia may speak English more comfortably than Malayalam. Kerala may be somewhere visited during summer vacations rather than somewhere experienced every day.

But put that child into a chenda ensemble and something changes.

Rhythm does not require translation.

A young Malayalee does not need perfect Malayalam to learn a rhythmic sequence from an older teacher. A grandparent and grandchild can participate in the same ensemble.

The instrument therefore becomes an unlikely vehicle for transmitting identity.

Not through a lecture about heritage.
Not through a history book.

But through participation.

Pick up the sticks. Learn the rhythm. Stand beside the others. Play.

Culture is suddenly something you do, rather than something you are merely told to preserve.`,
    },
    {
      h: "Tradition Is Changing—And That Is Part of the Story",
      t: `The globalization of chenda inevitably raises interesting questions.

When an instrument with deep ritual and artistic traditions becomes a recreational activity, how much of the original discipline should be preserved?

Traditional chenda performance involves years of learning, rhythmic knowledge, physical endurance and respect for established systems. A weekend workshop cannot reproduce generations of expertise.

That distinction deserves respect.

The extraordinary masters and traditional communities who developed and preserved Kerala's percussion heritage should remain central to its story.

At the same time, culture survives partly because people continue to adopt it.

Today's overseas chenda enthusiast may never become a master percussionist. But that person may introduce the instrument to 20 children who otherwise would never have encountered it.

The two worlds do not have to compete.

One preserves excellence.
The other expands participation.`,
    },
    {
      h: "Kerala's Heartbeat Has Acquired a Passport",
      t: `Perhaps that is why the chenda has succeeded so spectacularly outside Kerala.

It is portable—but powerful.
Traditional—but adaptable.
Religious in its historical associations—yet increasingly cultural in its modern reach.
Individual in the skill required to play it—yet profoundly communal when performed as a melam.

And above everything else, it is unmistakably Malayalee.

The sound that once echoed mainly around Kerala's temple grounds can now reverberate through American convention centers, British community halls, European festival grounds, Australian Malayalee gatherings and church celebrations around the world.

And somewhere, inevitably, another group is practicing.

One chenda begins.
Another answers.
Twenty join.
Then fifty.
Someone says they need a hundred next year.

And so Kerala's great percussion tradition continues its unlikely journey around the planet—one deafening, exhilarating beat at a time.

The Malayalee may leave Kerala.
But apparently, Kerala's rhythm refuses to stay behind.`,
    },
  ],
  links: [
    { label: "Thrissur Pooram — District Tourism Promotion Council, Thrissur", url: "https://dtpcthrissur.com/" },
    { label: "Thrissur Pooram — Incredible India, Ministry of Tourism", url: "https://www.incredibleindia.gov.in/en/kerala/thrissur/thrissur-pooram" },
    { label: "Thrissur Pooram — Utsav, Ministry of Tourism", url: "https://utsav.gov.in/view-event/thrissur-pooram" },
    { label: "FOKANA Pennsylvania Chenda Melam and Guinness achievement — Manorama Online", url: "https://www.manoramaonline.com/" },
    { label: "FOKANA's Guinness World Record Chenda Melam initiative — Manorama Online", url: "https://www.onmanorama.com/" },
  ],
};
